import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useAppStore } from '@/stores/appStore';
import { useAuth } from '@/contexts/AuthContext';
import { Navbar } from '@/components/layout/Navbar';
import { KanbanView } from '@/components/kanban/KanbanView';
import { KanbanColumnSettings } from '@/components/kanban/KanbanColumnSettings';
import { buildKanbanColumns, type KanbanColumn } from '@/components/kanban/columns';
import { FilterBar } from '@/components/filters/FilterBar';
import { Cog6ToothIcon } from '@heroicons/react/24/outline';
import {
  productApi,
  subProductApi,
  initiativeApi,
  classificationApi,
  statusApi,
  tagApi,
  userApi,
  fiscalYearApi,
  fieldConfigApi,
  labsApi,
  viewApi,
} from '@/services/api';

export function KanbanPage() {
  const {
    setProducts,
    setInitiatives,
    setClassifications,
    setStatuses,
    setTags,
    setOwners,
    setQuarters,
    setFieldConfigs,
    setLabsFeatures,
    setLoading,
    setError,
    refreshTrigger,
    statuses,
    triggerRefresh,
    activeSavedView,
    setActiveSavedView,
  } = useAppStore();
  const { user } = useAuth();

  const [filteredInitiatives, setFilteredInitiatives] = useState<any[]>([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [columnsModalOpen, setColumnsModalOpen] = useState(false);
  const [columns, setColumns] = useState<KanbanColumn[]>([]);
  // A column change with no active named view can't be saved silently — it's held here (applied
  // locally, lost on navigating away) and handed to FilterBar as pendingExtraFilterState, which
  // shows a "Save As…" hint next to the view-actions icon rather than forcing a save prompt.
  const [pendingSaveAsColumns, setPendingSaveAsColumns] = useState<KanbanColumn[] | null>(null);
  const viewContainerRef = useRef<HTMLDivElement>(null);

  // Editors/Admins can customize columns; Viewers see a read-only board.
  const canEditColumns = user?.role === 'EDITOR' || user?.role === 'ADMIN';

  // Column config lives on the server, inside the active named Saved View's filterState —
  // there's no such thing as a saved default layout. Absent config → derive columns straight
  // from the Statuses list.
  const savedCols: KanbanColumn[] | undefined = useMemo(() => {
    if (activeSavedView?.viewType === 'kanban') return activeSavedView.filterState.kanbanColumns;
    return pendingSaveAsColumns ?? undefined;
  }, [activeSavedView, pendingSaveAsColumns]);

  useEffect(() => {
    setColumns(buildKanbanColumns(statuses, savedCols));
  }, [statuses, savedCols]);

  // Once a named Kanban view is active, any pending unsaved column change is moot — it's
  // either been folded into the view that was just saved, or superseded by switching views.
  useEffect(() => {
    if (activeSavedView?.viewType === 'kanban' && pendingSaveAsColumns) {
      setPendingSaveAsColumns(null);
    }
  }, [activeSavedView, pendingSaveAsColumns]);

  const persistColumns = useCallback(
    (next: KanbanColumn[]) => {
      const normalized = next.map((c, i) => ({ ...c, order: i }));
      setColumns(normalized);

      if (activeSavedView?.viewType === 'kanban') {
        const updatedFilterState = { ...activeSavedView.filterState, kanbanColumns: normalized };
        viewApi
          .update(activeSavedView.id, { filterState: updatedFilterState })
          .then(() => setActiveSavedView({ ...activeSavedView, filterState: updatedFilterState }))
          .catch(() => {});
        return;
      }

      // No named view active — the default view can't be overwritten with a column layout.
      // Hold the change locally; FilterBar shows a "Save As…" hint so the user can keep it,
      // and it's simply lost (like any other unsaved edit) if they navigate away instead.
      setPendingSaveAsColumns(normalized);
    },
    [activeSavedView, setActiveSavedView]
  );

  // Drag a card to another column → change its status (optimistic; revert on failure)
  const handleMoveInitiative = useCallback(
    (initiativeId: string, newStatusId: string) => {
      const newStatus = statuses.find((s: any) => s.id === newStatusId);
      const current = useAppStore.getState().initiatives;
      setInitiatives(
        current.map((i: any) =>
          i.id === initiativeId ? { ...i, statusId: newStatusId, status: newStatus ?? i.status } : i
        )
      );
      initiativeApi.update(initiativeId, { statusId: newStatusId }).catch(() => {
        // Revert to server truth if the write failed (e.g. permission)
        triggerRefresh();
      });
    },
    [statuses, setInitiatives, triggerRefresh]
  );

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      setDataLoading(true);

      const [
        productsData,
        subProductsData,
        initiativesData,
        classificationsData,
        statusesData,
        tagsData,
        usersData,
        fiscalYearsData,
        fieldConfigData,
        labsData,
      ] = await Promise.all([
        productApi.getAll(),
        subProductApi.getAll(),
        initiativeApi.getAll(),
        classificationApi.getAll(),
        statusApi.getAll(),
        tagApi.getAll(),
        userApi.getAll(),
        fiscalYearApi.getAll(),
        fieldConfigApi.getAll().catch(() => []),
        labsApi.getAll().catch(() => []),
      ]);

      const productsWithSubProducts = productsData.map((product: any) => ({
        ...product,
        subProducts: subProductsData.filter((sp: any) => sp.productId === product.id),
      }));

      setProducts(productsWithSubProducts);
      setInitiatives(initiativesData);
      setClassifications(classificationsData);
      setStatuses(statusesData);
      setTags(tagsData);
      setOwners(usersData);
      if (fieldConfigData.length > 0) {
        setFieldConfigs(fieldConfigData);
      }
      if (labsData.length > 0) {
        setLabsFeatures(labsData);
      }

      const allQuarters = fiscalYearsData
        .flatMap((fy: any) => fy.quarters || [])
        .sort((a: any, b: any) => a.displayOrder - b.displayOrder);

      setQuarters(allQuarters);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load roadmap data');
    } finally {
      setLoading(false);
      setDataLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [refreshTrigger]);

  return (
    <div className="h-screen flex flex-col bg-background-primary">
      <Navbar />

      <main className="flex-1 overflow-hidden">
        <div className="h-full flex flex-col">
          <div className="px-3 sm:px-6 py-3 bg-background-primary flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
            <div className="flex-1 min-w-0">
              <FilterBar
                key={activeSavedView?.viewType === 'kanban' ? activeSavedView.id : 'default'}
                onFilteredInitiativesChange={setFilteredInitiatives}
                viewType="kanban"
                viewContainerRef={viewContainerRef}
                savedViewKey="kanban"
                hideExport
                injectedFilterState={activeSavedView?.viewType === 'kanban' ? activeSavedView.filterState : null}
                pendingExtraFilterState={
                  activeSavedView?.viewType !== 'kanban' && pendingSaveAsColumns
                    ? { kanbanColumns: pendingSaveAsColumns }
                    : undefined
                }
              />
            </div>
            {/* Configure columns */}
            {canEditColumns && (
              <button
                onClick={() => setColumnsModalOpen(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium bg-gray-100 text-text-secondary hover:bg-gray-200 hover:text-text-primary transition-colors flex-shrink-0"
                title="Configure columns"
              >
                <Cog6ToothIcon className="w-4 h-4" />
                <span className="hidden xl:inline">Columns</span>
              </button>
            )}
          </div>

          <div ref={viewContainerRef} className="flex-1 overflow-hidden">
            {dataLoading ? (
              <div className="h-full flex items-center justify-center text-text-secondary text-sm">Loading...</div>
            ) : (
              <KanbanView
                initiatives={filteredInitiatives}
                columns={columns}
                canEditColumns={canEditColumns}
                onColumnsChange={persistColumns}
                onMoveInitiative={handleMoveInitiative}
              />
            )}
          </div>
        </div>
      </main>

      <KanbanColumnSettings
        isOpen={columnsModalOpen}
        onClose={() => setColumnsModalOpen(false)}
        columns={columns}
        statuses={statuses}
        onSave={persistColumns}
      />
    </div>
  );
}
